import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPaperPlane,
  faRedo,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import ApiServices from "../Services/ApiServices";
import Loader from "../Shared/Loader";

const OTPSender = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const stateData = location.state || {};
  const email =
    stateData.email ||
    new URLSearchParams(window.location.search).get("email") ||
    "";
  const phone =
    stateData.phone || stateData.phone_number ||
    new URLSearchParams(window.location.search).get("phone") ||
    "";
  const personal_name =
    stateData.personal_name ||
    new URLSearchParams(window.location.search).get("personal_name") ||
    "";

  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [isWhatsAppActive, setIsWhatsAppActive] = useState(true);
  const hasSentRef = useRef(false);

  // Send OTP on mount
  useEffect(() => {
    if ((email || phone) && !hasSentRef.current) {
      hasSentRef.current = true;
      sendOTP(true);
    }
    // eslint-disable-next-line
  }, [email, phone]);

  const sendOTP = async (checkStatusFirst = false) => {
    setSending(true);
    setError("");
    setSuccessMsg("");
    try {
      let activeService = isWhatsAppActive;
      
      if (checkStatusFirst) {
        try {
          const status = await ApiServices.get("WhatsApp/CheckStatus");
          activeService = status === true;
          setIsWhatsAppActive(activeService);
        } catch (err) {
          console.error("Error checking WhatsApp status", err);
          activeService = false;
          setIsWhatsAppActive(false);
        }
      }

      const promises = [];
      if (activeService && phone) {
        promises.push(ApiServices.post("WhatsApp/SendOTP", { phone }));
      }
      if (email) {
        promises.push(ApiServices.post("Email/SendOTP", { email, personal_name }));
      }

      if (promises.length > 0) {
        await Promise.all(promises);
        if (!checkStatusFirst) {
          setSuccessMsg("تم إعادة إرسال رمز التحقق بنجاح");
        }
      } else {
        throw new Error("لا يوجد بريد إلكتروني أو رقم هاتف لإرسال الرمز");
      }

    } catch (err) {
      setError(err?.msg || err?.message || "حدث خطأ أثناء إرسال رمز التحقق");
    } finally {
      setSending(false);
    }
  };

  // Verify handler
  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError("");
    setSuccessMsg("");
    try {
      let result = false;
      let verifyError = null;

      if (isWhatsAppActive && phone) {
        try {
          result = await ApiServices.post("WhatsApp/CheckOTP", { otp, phone });
        } catch (err) {
          verifyError = err;
        }
      }
      
      if (result !== true && email) {
        try {
          result = await ApiServices.post("Email/CheckOTP", { otp, email });
        } catch (err) {
          verifyError = err;
        }
      }
      
      if (result === true) {
        navigate("/signin");
      } else {
        setError(verifyError?.msg || "رمز التحقق غير صحيح أو منتهي الصلاحية");
      }
    } catch (err) {
      setError(err?.msg || "حدث خطأ أثناء التحقق من الرمز");
    } finally {
      setVerifying(false);
    }
  };

  if (!email && !phone) {
    return <Loader text="يرجى العودة وإعادة التسجيل. البيانات غير مكتملة." />;
  }

  return (
    <motion.section
      className="otp-section"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      dir="rtl"
      style={{
        position: "relative",
        overflow: "hidden",
        background: "radial-gradient(circle at 12% 8%, rgba(124, 58, 237, 0.08) 0, transparent 36%), radial-gradient(circle at 90% 22%, rgba(37, 99, 235, 0.07) 0, transparent 40%), linear-gradient(180deg, #f8faff 0%, #f6f8fc 45%, #f9fafc 100%)",
        minHeight: "100vh",
        padding: "0",
        margin: 0,
      }}
    >
      <div
        style={{ maxWidth: 420, margin: "0 auto", padding: "64px 0 32px 0" }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#6d5bd0",
            fontWeight: 900,
            marginBottom: 24,
            fontSize: 30,
            letterSpacing: 1,
          }}
        >
          <FontAwesomeIcon
            icon={isWhatsAppActive && phone ? faWhatsapp : faEnvelope}
            style={{ marginLeft: 12, fontSize: 26, color: isWhatsAppActive && phone ? "#25D366" : "#3b82f6" }}
          />
          تحقق من حسابك
        </h2>
        <div
          style={{
            textAlign: "center",
            color: "#555",
            marginBottom: 18,
            fontSize: 17,
          }}
        >
          {isWhatsAppActive && phone && email ? (
            <>
              تم إرسال رمز تحقق مكون من 6 أرقام عبر WhatsApp والبريد الإلكتروني إلى:
              <div style={{ color: "#2563eb", fontWeight: 700, marginTop: 6, direction: "ltr" }}>
                {phone}
              </div>
              <div style={{ color: "#2563eb", fontWeight: 700, marginTop: 6 }}>
                {email}
              </div>
            </>
          ) : (
            <>
              تم إرسال رمز تحقق مكون من 6 أرقام عبر {isWhatsAppActive && phone ? "WhatsApp" : "البريد الإلكتروني"} إلى {isWhatsAppActive && phone ? "الرقم:" : "البريد:"}
              <div style={{ color: "#2563eb", fontWeight: 700, marginTop: 6, direction: isWhatsAppActive && phone ? "ltr" : "rtl" }}>
                {isWhatsAppActive && phone ? phone : email}
              </div>
            </>
          )}
        </div>
        {sending ? (
          <Loader text="جاري إرسال رمز التحقق..." />
        ) : (
          <form
            onSubmit={handleVerify}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 24,
              marginTop: 18,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label
                htmlFor="otp"
                style={{ color: "#6d5bd0", fontWeight: 600, fontSize: 16 }}
              >
                رمز التحقق
              </label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="أدخل رمز التحقق المكون من 6 أرقام"
                style={{
                  width: "100%",
                  padding: "18px 20px",
                  borderRadius: 10,
                  border: "1.5px solid #e0e7ff",
                  fontSize: "1.25rem",
                  fontFamily: "inherit",
                  background: "#f8f9fa",
                  color: "#222",
                  textAlign: "center",
                  letterSpacing: 8,
                  fontWeight: 700,
                  outline: "none",
                  transition: "border 0.2s",
                }}
                autoFocus
              />
            </div>
            {error && (
              <div
                style={{
                  color: "#e74c3c",
                  fontWeight: 600,
                  textAlign: "center",
                  fontSize: 16,
                }}
              >
                {error}
              </div>
            )}
            {successMsg && (
              <div
                style={{
                  color: "#2ecc71",
                  fontWeight: 700,
                  textAlign: "center",
                  fontSize: 16,
                }}
              >
                {successMsg}
              </div>
            )}
            <motion.button
              type="submit"
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.98 }}
              disabled={verifying || otp.length !== 6}
              style={{
                background: "linear-gradient(135deg, #6d5bd0 0%, #8b5cf6 45%, #38bdf8 100%)",
                color: "#fff",
                fontWeight: 800,
                fontSize: 19,
                border: "none",
                borderRadius: 12,
                padding: "15px 0",
                marginTop: 6,
                boxShadow: "0 10px 20px rgba(109, 91, 208, 0.25)",
                cursor: verifying ? "not-allowed" : "pointer",
                width: "100%",
                transition: "all 0.25s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                letterSpacing: 1,
              }}
            >
              {verifying ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faPaperPlane} />
              )}
              {verifying ? "جاري التحقق..." : "تأكيد الرمز"}
            </motion.button>
            <button
              type="button"
              onClick={() => sendOTP(false)}
              disabled={sending}
              style={{
                background: "none",
                color: "#6d5bd0",
                border: "none",
                fontWeight: 700,
                fontSize: 16,
                marginTop: 8,
                cursor: sending ? "not-allowed" : "pointer",
                textDecoration: "underline",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon icon={faRedo} />
              إعادة إرسال الرمز
            </button>
          </form>
        )}
      </div>
    </motion.section>
  );
};

export default OTPSender;
